import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { DataTable } from '../../components/DataTable';
import { ErrorBanner } from '../../components/ErrorBanner';
import { Field, TextInput } from '../../components/Field';
import { goodsVouchersApi, type DownloadedFile } from '../../lib/api';
import type { GoodsDirection, GoodsVoucherRegister, GoodsVoucherRegisterItem } from '../../lib/types';

const today = () => new Date().toISOString().slice(0, 10);
const monthStart = () => `${today().slice(0, 7)}-01`;
function saveDownload(file: DownloadedFile) {
  const url = URL.createObjectURL(file.blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = file.filename;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Chrome may consume the download asynchronously after click().
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  return file.filename;
}

/** A narrow, keyboard-navigable register backed only by the server register. */
export function GoodsVoucherRegisterPage({ direction }: { direction: GoodsDirection }) {
  const navigate = useNavigate();
  const title = direction === 'sales' ? 'Sales register' : 'Purchase register';
  const [from, setFrom] = useState(monthStart);
  const [to, setTo] = useState(today);
  const [register, setRegister] = useState<GoodsVoucherRegister | null>(null);
  const [monthFilter, setMonthFilter] = useState<string | null>(null);
  const rows: GoodsVoucherRegisterItem[] = (register?.items ?? []).filter(
    (row) => !monthFilter || row.voucherDate.startsWith(monthFilter)
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [exporting, setExporting] = useState<'xlsx' | 'pdf' | null>(null);
  const [downloadStatus, setDownloadStatus] = useState<string | null>(null);
  const load = () => {
    if (from > to) {
      setRegister(null);
      setMonthFilter(null);
      setLoading(false);
      setExporting(null);
      setDownloadStatus(null);
      setError(new Error('The register end date cannot be before the start date.'));
      return;
    }
    setLoading(true);
    setError(null);
    goodsVouchersApi
      .register(direction, from, to)
      .then((result) => {
        setRegister(result);
        setMonthFilter(null);
      })
      .catch(setError)
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load(); // deliberate initial snapshot only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [direction]);
  async function exportRegister(format: 'xlsx' | 'pdf') {
    setExporting(format);
    setDownloadStatus(null);
    try {
      const file = await goodsVouchersApi.exportRegister(direction, from, to, format);
      setDownloadStatus(`Download started: ${saveDownload(file)}`);
    } catch (cause) {
      setError(cause);
    } finally {
      setExporting(null);
    }
  }
  return (
    <div className="flex min-h-full flex-col bg-[var(--bg)]">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--border)] bg-[var(--bg-elevated)] px-4 py-2">
        <div>
          <p className="text-[10.5px] uppercase tracking-[.12em] text-[var(--accent)]">Goods books</p>
          <h1 className="text-[15px] font-semibold">{title}</h1>
        </div>
        <div className="flex gap-2">
          <button className="button-muted" onClick={() => void exportRegister('xlsx')} disabled={!rows.length || exporting !== null}>
            {exporting === 'xlsx' ? 'Preparing XLSX…' : 'Export XLSX'}
          </button>
          <button className="button-muted" onClick={() => void exportRegister('pdf')} disabled={!rows.length || exporting !== null}>
            {exporting === 'pdf' ? 'Preparing PDF…' : 'Export PDF'}
          </button>
          <button className="button-primary" onClick={() => navigate(`/goods/${direction}/new`)}>
            New {direction === 'sales' ? 'sales invoice' : 'purchase'}{' '}
            <span className="kbd">{direction === 'sales' ? 'F8' : 'F9'}</span>
          </button>
        </div>
      </header>
      {Boolean(error) && (
        <div className="px-4 pt-3">
          <ErrorBanner error={error} onDismiss={() => setError(null)} />
        </div>
      )}
      {downloadStatus && (
        <p className="px-4 pt-2 text-[11px] text-[var(--success)]" role="status" aria-live="polite">
          {downloadStatus}
        </p>
      )}
      <main className="flex min-h-0 flex-1 flex-col gap-3 p-4">
        <div className="flex flex-wrap items-end gap-2 border border-[var(--border)] bg-[var(--bg-elevated)] p-2">
          <Field label="From">
            <TextInput type="date" value={from} onChange={(event) => setFrom(event.target.value)} />
          </Field>
          <Field label="To">
            <TextInput type="date" value={to} onChange={(event) => setTo(event.target.value)} />
          </Field>
          <button className="button-primary mb-px" onClick={load} disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
          <p className="ml-auto text-[11px] text-[var(--text-faint)]">↑↓ selects · Enter opens document</p>
        </div>
        {register && (
          <section className="grid grid-cols-1 gap-px border border-[var(--border)] bg-[var(--border)] text-[11.5px] sm:grid-cols-4">
            <div className="bg-[var(--bg-elevated)] px-3 py-2">
              <span className="text-[var(--text-faint)]">Net assessable value</span>
              <b className="num ml-2">{register.totals.netAssessableValue}</b>
            </div>
            <div className="bg-[var(--bg-elevated)] px-3 py-2">
              <span className="text-[var(--text-faint)]">Gross total</span>
              <b className="num ml-2">{register.totals.grossTotal}</b>
            </div>
            <div className="bg-[var(--bg-elevated)] px-3 py-2">
              <span className="text-[var(--text-faint)]">Net posted</span>
              <b className="num ml-2 text-[var(--success)]">{register.totals.netTotal}</b>
            </div>
            <div className="bg-[var(--bg-elevated)] px-3 py-2">
              <span className="text-[var(--text-faint)]">Cancelled</span>
              <b className="num ml-2">{register.totals.cancelledCount}</b>
            </div>
          </section>
        )}
        {register?.monthlySummary.length ? (
          <section className="overflow-auto border border-[var(--border)] bg-[var(--bg-elevated)]">
            <div className="grid min-w-[480px] grid-cols-4 gap-2 border-b border-[var(--border)] bg-[var(--bg-inset)] px-3 py-1 text-[10px] uppercase tracking-wide text-[var(--text-dim)]">
              <span>Month</span>
              <span>Documents / cancelled</span>
              <span className="text-right">Gross</span>
              <span className="text-right">Net posted</span>
            </div>
            {register.monthlySummary.map((month) => (
              <button
                key={month.month}
                type="button"
                onClick={() => setMonthFilter(month.month)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setMonthFilter(month.month);
                  }
                }}
                className={clsx(
                  'grid min-w-[480px] grid-cols-4 gap-2 border-b border-[var(--border)] px-3 py-1.5 text-left text-[11.5px] last:border-0 focus-visible:outline focus-visible:outline-1 focus-visible:outline-[var(--focus-ring)]',
                  monthFilter === month.month && 'bg-[var(--bg-selected)]'
                )}
              >
                <span>{month.month}</span>
                <span>
                  {month.count} / {month.cancelledCount}
                </span>
                <span className="num text-right">{month.grossTotal}</span>
                <span className="num text-right text-[var(--success)]">{month.netTotal}</span>
              </button>
            ))}
          </section>
        ) : null}
        {monthFilter && (
          <div className="flex items-center justify-between text-[11.5px] text-[var(--text-dim)]">
            <span>Showing vouchers for {monthFilter}.</span>
            <button className="button-muted" onClick={() => setMonthFilter(null)}>
              All months
            </button>
          </div>
        )}
        <DataTable
          rows={rows}
          getRowKey={(row) => row.id}
          onActivate={(row) => navigate(`/goods/${direction}/${row.id}`)}
          emptyMessage={
            loading ? `Loading ${title.toLowerCase()}…` : `No ${title.toLowerCase()} documents in this period.`
          }
          columns={[
            {
              key: 'date',
              header: 'Date',
              render: (row) => row.voucherDate,
              widthClassName: 'w-28',
            },
            {
              key: 'number',
              header: 'Document',
              render: (row) => <span className="font-medium">{row.displayNumber}</span>,
              widthClassName: 'w-48',
            },
            {
              key: 'party',
              header: direction === 'sales' ? 'Customer' : 'Supplier',
              render: (row) => row.partyName,
            },
            {
              key: 'taxable',
              header: 'Assessable value',
              numeric: true,
              render: (row) => row.totals?.assessableValue ?? row.totals?.gstTaxableValue ?? '—',
              widthClassName: 'w-36',
            },
            {
              key: 'payable',
              header: direction === 'sales' ? 'Receivable' : 'Payable',
              numeric: true,
              render: (row) => row.totals?.counterpartyPayable ?? '—',
              widthClassName: 'w-36',
            },
            {
              key: 'state',
              header: 'Status',
              render: (row) =>
                row.cancelledAt ? (
                  <span className="badge-danger">Cancelled</span>
                ) : (
                  <span className="text-[var(--success)]">Posted</span>
                ),
              widthClassName: 'w-24',
            },
          ]}
        />
      </main>
    </div>
  );
}
